import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import { ArrowBack, BookmarkBorder, Bookmark } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import Spinner from '../components/Spinner';
import { readSchoolBookmarks, toggleSchoolBookmark } from '../utils/schoolBookmarks';

interface QuizItem {
  _id: string;
  title: string;
  subject: { _id: string; name: string };
  questions: any[];
  totalPoints: number;
  dueDate?: string;
  selectedStudents?: string[];
}

const StudentQuizzes: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quizzes, setQuizzes] = useState<QuizItem[]>([]);
  const [selectedSubjectId, setSelectedSubjectId] = useState<string | null>(null);
  const [bookmarkedIds, setBookmarkedIds] = useState<Record<string, boolean>>({});

  const refreshBookmarks = async () => {
    const saved = await readSchoolBookmarks();
    const next: Record<string, boolean> = {};
    saved.forEach((bookmark) => {
      next[`${bookmark.type}:${bookmark.id}`] = true;
    });
    setBookmarkedIds(next);
  };

  const getQuizSubjectGroupKey = (quiz: QuizItem) => {
    const name = quiz.subject?.name?.trim();
    return name ? name.toLowerCase() : 'general';
  };

  const getQuizSubjectGroupLabel = (quiz: QuizItem) => {
    const name = quiz.subject?.name?.trim();
    return name || 'General/Uncategorized';
  };

  const isHighSchoolEducation = (educationLevel?: string) =>
    typeof educationLevel === 'string' && /high[- ]school/i.test(educationLevel);

  const hasSchoolWorkAccess = Boolean(
    isHighSchoolEducation(user?.educationLevel) &&
    (user?.school || user?.schoolName || user?.schoolEmail)
  );

  useEffect(() => {
    const fetchQuizzes = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get('/school-work/quizzes');
        setQuizzes(Array.isArray(res.data) ? res.data : []);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Unable to load quizzes');
      } finally {
        setLoading(false);
      }
    };
    
    if (hasSchoolWorkAccess) {
      fetchQuizzes();
      refreshBookmarks().catch(() => setError('Unable to load saved bookmarks'));
    } else {
      setLoading(false);
    }
  }, [hasSchoolWorkAccess, user?._id]);

  const handleToggleBookmark = async (quiz: QuizItem) => {
    try {
      const isBookmarked = await toggleSchoolBookmark({
      id: quiz._id,
      type: 'quiz',
      title: quiz.title,
      subject: quiz.subject?.name || 'General',
      route: `/school-work/quiz/${quiz._id}`,
      details: `${quiz.questions?.length || 0} questions • ${quiz.totalPoints || 0} points`,
      savedAt: new Date().toISOString(),
      });
      setBookmarkedIds((current) => ({ ...current, [`quiz:${quiz._id}`]: isBookmarked }));
    } catch {
      setError('Unable to update saved quiz. Please sign in and try again.');
    }
  };

  if (!hasSchoolWorkAccess && !loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          School Work is available only to high school students...
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Quizzes</Typography>
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate('/school-work')}>
          Back to Dashboard
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <Spinner />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      ) : (
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>Quizzes published by your teachers</Typography>
          {quizzes.length === 0 ? (
            <Alert severity="info">No published quizzes available yet.</Alert>
          ) : !selectedSubjectId ? (
            <Grid container spacing={3}>
              {Object.values(
                quizzes.reduce((acc, quiz) => {
                  const subjectKey = getQuizSubjectGroupKey(quiz);
                  const subjectName = getQuizSubjectGroupLabel(quiz);
                  if (!acc[subjectKey]) {
                    acc[subjectKey] = { _id: subjectKey, name: subjectName, count: 0 };
                  }
                  acc[subjectKey].count += 1;
                  return acc;
                }, {} as Record<string, { _id: string; name: string; count: number }>)
              )
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((subject: any) => (
                <Grid item xs={12} sm={6} md={4} key={subject._id}>
                  <Card 
                    sx={{ cursor: 'pointer', transition: '0.2s', '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 } }}
                    onClick={() => setSelectedSubjectId(subject._id)}
                  >
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 1 }}>{subject.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {subject.count} Quiz{subject.count !== 1 ? 'zes' : ''}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box>
              <Button sx={{ mb: 2 }} onClick={() => setSelectedSubjectId(null)} startIcon={<ArrowBack />}>
                Back to Subjects
              </Button>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Title</TableCell>
                      <TableCell>Subject</TableCell>
                      <TableCell>Questions</TableCell>
                      <TableCell>Points</TableCell>
                      <TableCell>Due Date</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {quizzes
                      .filter(q => getQuizSubjectGroupKey(q) === selectedSubjectId)
                      .map((quiz) => (
                      <TableRow key={quiz._id}>
                        <TableCell>
                          {quiz.title}
                          {Array.isArray(quiz.selectedStudents) && quiz.selectedStudents.length > 0 && (
                            <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.5 }}>
                              Quiz visible only to selected students
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{quiz.subject?.name || 'General'}</TableCell>
                        <TableCell>{quiz.questions?.length || 0}</TableCell>
                        <TableCell>{quiz.totalPoints}</TableCell>
                        <TableCell>{quiz.dueDate ? new Date(quiz.dueDate).toLocaleDateString() : 'No deadline'}</TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
                            <Button
                              size="small"
                              variant={bookmarkedIds[`quiz:${quiz._id}`] ? 'contained' : 'outlined'}
                              color="warning"
                              startIcon={bookmarkedIds[`quiz:${quiz._id}`] ? <Bookmark /> : <BookmarkBorder />}
                              onClick={() => handleToggleBookmark(quiz)}
                            >
                              {bookmarkedIds[`quiz:${quiz._id}`] ? 'Saved' : 'Save'}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => navigate(`/school-work/quiz/${quiz._id}`)}
                            >
                              View
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default StudentQuizzes;
